import { useState } from "react";

import { useNavigate } from "@Libs/Router/hooks/useNavigate";
import { useSearchParams } from "@Libs/Router/hooks/useSearchParams";

import { serviceRequestPathFragment } from "../../config/routes";
import { SUBCATEGORY_SCREEN_KEY } from "../config/constants";
import {
  clearDraftStorage,
  getDraftToken,
  getLastScreen,
} from "./useDraftStorage";

export interface IUseOngoingDraftGuardResult {
  isOpen: boolean;
  /** Also the modal's `onClose`: dismissing must not orphan the draft. */
  onContinue: () => void;
  onDiscard: () => void;
}

/**
 * The description screen's guard against orphaning an ongoing draft (RT-4321).
 *
 * The entry screen is reachable at any time — an edited URL, a refresh, a
 * reopened tab — and its submit mints a *new* draft and a new public token. So
 * a device that already holds one is asked first: resume it, or throw it away
 * and start over.
 *
 * Storage is read once, on mount, rather than on every render: `onDiscard` has
 * to close the modal, and a render-time read would reopen it against a token
 * that is only just gone.
 */
export const useOngoingDraftGuard = (): IUseOngoingDraftGuardResult => {
  const navigate = useNavigate();
  // Forwarded on resume for the same reason the submit path forwards them
  // (`DescriptionScreen.onSubmit`): `navigate` builds the path from `params`
  // alone, and the flow screens read the theme off the URL — the draft cannot
  // name it until a subcategory is picked.
  const pCategory = useSearchParams("p_category");
  const sCategory = useSearchParams("s_category");
  const [isOpen, setIsOpen] = useState(() => Boolean(getDraftToken()));

  return {
    isOpen,
    // `getLastScreen()` is the flow's own record of where the user stood, and
    // the picker is the floor: it is the one screen every flow has, and the
    // only one that exists before a subcategory is chosen. Nothing here
    // validates the key against the flow shape — `useTaskCreationFlow()`
    // reports no screens at all on this route (`deriveFlowState` returns the
    // empty state whenever there is no `screenKey`), so there is nothing to
    // validate against. An unknown key lands on `DynamicScreen`, which renders
    // its loading state for a screen it cannot find, exactly as a refresh does.
    onContinue: () =>
      navigate(serviceRequestPathFragment.SERVICE_REQUEST_CREATION_SCREEN, {
        withLocale: true,
        params: { screenKey: getLastScreen() || SUBCATEGORY_SCREEN_KEY },
        queryParams: {
          ...(pCategory ? { p_category: pCategory } : {}),
          ...(sCategory ? { s_category: sCategory } : {}),
        },
      }),
    onDiscard: () => {
      clearDraftStorage();
      setIsOpen(false);
    },
  };
};
